import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLock } from '@fortawesome/free-solid-svg-icons';
import { getSportIcon } from '../utils/sportEmoji';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import { getEloColor } from '../utils/ladder';
import logo from '../assets/logo.png';
import EloHistoryChart from '../components/EloHistoryChart';
import SportBreakdownBar from '../components/SportBreakdownBar';
import WeeklyCalendar from '../components/WeeklyCalendar';
import { DailyChallenge } from '../types/activity';
import '../styles/ProfilePage.css';
import '../styles/SportBreakdown.css';

interface ZoneRange { min: number; max: number; }
interface HrZones {
  athlete_zones_raw: { min: number; max: number }[] | null;
  calculated_max_hr: number;
  zone_ranges: Record<string, ZoneRange>;
  zone_percentages: [number, number][];
}
interface Props { onBack: () => void; onReplay?: (matchupId: number) => void; viewPlayerId?: number | null; }

const ZONE_NAMES = ['Rest', 'Recovery', 'Endurance', 'Tempo', 'Threshold', 'Anaerobic'];
const ZONE_COLORS = ['#6b7280', '#3b82f6', '#22c55e', '#eab308', '#f97316', '#ef4444'];

function getBadgeCurrentValue(badge: any): string {
  const tier = badge.current_tier || 0;
  const nextTier = tier + 1;
  const val = badge.tier_kriterien?.[nextTier]?.value ?? badge.tier_kriterien?.[1]?.value ?? 0;
  const type = badge.tier_kriterien?.[nextTier]?.type ?? badge.tier_kriterien?.[1]?.type ?? '';
  const cur = badge.current_value ?? 0;
  const reward = badge.tier_rewards?.[tier] ?? 0;

  let progress = '';
  switch (type) {
    case 'weekly_points': progress = `${cur}/${val} pts this week`; break;
    case 'win_streak': progress = `${cur} wins in a row`; break;
    case 'loss_streak': progress = `${cur} losses in a row`; break;
    case 'sport_groups': progress = `${cur}/${val} sport groups this week`; break;
    case 'items_used': progress = `${cur}/${val} items used this week`; break;
    case 'weeks_played': progress = `${cur}/${val} weeks played`; break;
  }

  if (tier === 0) return `${badge.description} — Your current status: ${progress}`;
  return `Your current status: ${progress} — +${reward} pts/week`;
}

function getBadgeTierDescription(badge: any, tier: number): string {
  const val = badge.tier_kriterien?.[tier]?.value ?? 0;
  const type = badge.tier_kriterien?.[tier]?.type ?? '';
  switch (type) {
    case 'weekly_points': return `Earn ${val}+ points in a single week`;
    case 'win_streak': return `Win ${val} games in a row`;
    case 'loss_streak': return `Lose ${val} games in a row`;
    case 'sport_groups': return `Train ${val} different sport groups (30+ min each)`;
    case 'items_used': return `Use ${val} items in a single week`;
    case 'weeks_played': return `Play ${val} calendar weeks`;
    default: return `Reach tier ${tier}`;
  }
}

function BadgeTimeline({ badge, activeTier, setActiveTier }: { badge: any; activeTier: { badgeCode: string; tier: number } | null; setActiveTier: (v: { badgeCode: string; tier: number } | null) => void }) {
  const yellowColor = '#e6b800';
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const myOpen = activeTier?.badgeCode === badge.code;

  useScrollDismiss(myOpen ? tooltipPos : null, () => setActiveTier(null));

  function showTT(e: React.MouseEvent<Element>, tier: number) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
    setActiveTier({ badgeCode: badge.code, tier });
  }
  function hideTT() { setActiveTier(null); setTooltipPos(null); }
  function toggleTT(e: React.MouseEvent<Element>, tier: number) {
    e.stopPropagation();
    if (myOpen && activeTier?.tier === tier) { setActiveTier(null); setTooltipPos(null); }
    else showTT(e, tier);
  }

  return (
    <div className="badge-timeline">
      <div className="badge-timeline-inner">
        {[1, 2, 3].map((tier, idx) => {
          const isTierEarned = badge.current_tier >= tier;
          const reward = badge.tier_rewards?.[tier] ?? 0;
          const kriterium = badge.tier_kriterien?.[tier];
          const reqValue = kriterium?.value ?? 0;
          return (
            <React.Fragment key={tier}>
              {idx > 0 && <div className="badge-timeline-line-flex" />}
              <div className="badge-timeline-item"
                onMouseEnter={(e) => !isTouch.current && showTT(e, tier)}
                onMouseLeave={() => !isTouch.current && hideTT()}
                onClick={(e) => toggleTT(e, tier)}
              >
                <div className="badge-timeline-icon" style={{
                  border: `2px solid ${isTierEarned ? yellowColor : '#444'}`,
                  background: isTierEarned ? 'rgba(230,184,0,0.15)' : 'transparent',
                  color: isTierEarned ? yellowColor : '#444',
                }}>
                  <FontAwesomeIcon icon={getSportIcon(badge.icon || 'BadgeReward')} />
                  {!isTierEarned && <FontAwesomeIcon icon={faLock} className="badge-lock" />}
                </div>
                {isTierEarned && (
                  <div className="item-tier-icons badge-tier-green">{'▲'.repeat(tier)}</div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {myOpen && tooltipPos && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: tooltipPos.y, left: tooltipPos.x, transform: 'translateX(-50%) translateY(-100%)', zIndex: 9999, pointerEvents: 'none' }}>
          <div className="badge-timeline-tooltip">
            <div className="badge-tooltip-name" style={{ color: yellowColor }}>Tier {activeTier!.tier}</div>
            <div style={{ fontSize: '0.65rem', color: '#ccc', marginTop: 2 }}>{getBadgeTierDescription(badge, activeTier!.tier)}</div>
            <div className="badge-tooltip-reward">Reward: +{(badge.tier_rewards?.[activeTier!.tier] ?? 0)} pts/week</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function ProfilePage({ onBack, onReplay, viewPlayerId }: Props) {
  const { token, playerId } = useAuth();
  const targetId = viewPlayerId || playerId;
  const [hrZones, setHrZones] = useState<HrZones | null>(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<any[]>([]);
  const [weekChallenges] = useState<Record<string, DailyChallenge>>({});
  const [sportsActivities, setSportsActivities] = useState<any[]>([]);
  const [zoneBreakdown, setZoneBreakdown] = useState<Record<string, number>>({});
  const [playerName, setPlayerName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [badges, setBadges] = useState<any[]>([]);
  const [activeTimelineTier, setActiveTimelineTier] = useState<{ badgeCode: string; tier: number } | null>(null);
  const [playerStats, setPlayerStats] = useState<any>(null);
  const [stravaAthleteId, setStravaAthleteId] = useState<number | null>(null);

  const viewPlayerIdRef = useRef(viewPlayerId);
  viewPlayerIdRef.current = viewPlayerId;

  const loadResults = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.get('/duel/archive?page=1&limit=20', token);
      if (data?.matches && viewPlayerIdRef.current === playerId) setResults(data.matches);
    } catch {}
  }, [token, playerId]);

  const weeklyChartData = useMemo(() => {
    if (results.length === 0 || viewPlayerId) return null;
    try {
    const grouped: Record<string, { myPts: number; oppPts: number; wins: number; losses: number }> = {};
    for (const m of results) {
      const wl = m.week_label || 'Unknown';
      if (!grouped[wl]) grouped[wl] = { myPts: 0, oppPts: 0, wins: 0, losses: 0 };
      grouped[wl].myPts += Number(m.my_points) || 0;
      grouped[wl].oppPts += Number(m.opp_points) || 0;
      if (m.result === 'win') grouped[wl].wins++;
      else if (m.result === 'loss') grouped[wl].losses++;
    }
    const weeks = Object.entries(grouped).slice(-10).reverse();
    if (weeks.length === 0) return null;
    const maxPts = Math.max(...weeks.map(([, d]) => Math.max(d.myPts, d.oppPts)), 1);
    const avg = weeks.reduce((s, [, d]) => s + d.myPts, 0) / weeks.length;
    const avgY = 180 - (avg / maxPts) * 150;
    return { weeks, maxPts, avg, avgY };
    } catch { return null; }
  }, [results, viewPlayerId]);

  useEffect(() => {
    if (!token) return;
    const isOwn = !viewPlayerId || viewPlayerId === playerId;

    if (isOwn) {
      // Eigene Daten laden
      api.get('/player/me/profile/zones', token).then(data => {
        if (data?.hr_zones) setHrZones(data.hr_zones);
        setLoading(false);
      });
      loadResults();
      api.get('/player/me/sports-summary', token).then(data => {
        if (data?.activities) setSportsActivities(data.activities);
        if (data?.zone_breakdown) setZoneBreakdown(data.zone_breakdown);
      });
      api.get('/player/me', token).then(data => {
        if (data?.display_name) setPlayerName(data.display_name);
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
        if (data?.strava_athlete_id) setStravaAthleteId(Number(data.strava_athlete_id));
      });
      // Stats wie beim Gegner-Profil laden (OnFire, W/L, Rang, etc.)
      api.get(`/player/${playerId}/profile`, token).then(data => {
        setPlayerStats({
          elo_rating: data.elo_rating,
          wins: data.wins,
          losses: data.losses,
          global_rank: data.global_rank,
          total_players: data.total_players,
          avg_weekly_pts: data.avg_weekly_pts,
          last_week_pts: data.last_week_pts,
        });
      }).catch(() => {});
      api.get('/player/me/badges', token).then(data => {
        if (data?.badges) setBadges(Array.isArray(data.badges) ? data.badges : Object.values(data.badges));
      });
    } else {
      // Gegner-Daten laden – eigene Daten zurücksetzen
      setResults([]);
      setHrZones(null);
      setZoneBreakdown({});
      setSportsActivities([]);
      setStravaAthleteId(null);
      api.get(`/player/${viewPlayerId}/profile`, token).then(data => {
        if (data?.display_name) setPlayerName(data.display_name);
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
        if (data?.strava_athlete_id) setStravaAthleteId(Number(data.strava_athlete_id));
        if (data?.sports) setSportsActivities(data.sports.flatMap((s: any) =>
          Array.from({ length: s.count }, () => ({ sport_type: s.sport, duration_seconds: s.avg_minutes * 60, total_points: s.avg_points, started_at_utc: new Date().toISOString().replace('T', ' ').slice(0, 19) }))
        ));
        if (data?.hr_zones) setHrZones(data.hr_zones);
        if (data?.zone_breakdown) setZoneBreakdown(data.zone_breakdown);
        setPlayerStats({
          elo_rating: data.elo_rating,
          wins: data.wins,
          losses: data.losses,
          global_rank: data.global_rank,
          total_players: data.total_players,
          avg_weekly_pts: data.avg_weekly_pts,
          last_week_pts: data.last_week_pts,
        });
        setLoading(false);
      }).catch(() => setLoading(false));
      api.get(`/player/${viewPlayerId}/badges`, token).then(data => {
        if (data?.badges) setBadges(Array.isArray(data.badges) ? data.badges : Object.values(data.badges));
      }).catch(() => {});
    }
  }, [token, viewPlayerId, playerId]);

  return (
    <div className="profile-page">
      <header className="archive-header">
        <div className="archive-header-nav">
          <button className="btn-back" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
        </div>
        {avatarUrl
          ? stravaAthleteId
            ? <a href={`https://www.strava.com/athletes/${stravaAthleteId}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><img src={avatarUrl} alt="" className="archive-logo" style={{ borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }} /></a>
            : <img src={avatarUrl} alt="" className="archive-logo" style={{ borderRadius: '50%', objectFit: 'cover' }} />
          : <img src={logo} alt="NoWeakWeek" className="archive-logo" />
        }
        <h1>{playerName ? `Profile of ${playerName}` : 'My Profile'}</h1>
      </header>

      {stravaAthleteId && (
        <div style={{ textAlign: 'center', margin: '-8px 0 16px' }}>
          <a href={`https://www.strava.com/athletes/${stravaAthleteId}`} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-block', padding: '8px 20px', background: '#fc4c02', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none' }}>
            Show Strava Profile
          </a>
        </div>
      )}

      {/* ── Player Stats (wie Tooltip) ── */}
      {playerStats && (
        <div className="profile-section" style={{ margin: '0 16px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ffb63c' }}>🔥 {playerStats.elo_rating}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>OnFire</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#4caf50' }}>#{playerStats.global_rank} / {playerStats.total_players}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Rank</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#60a5fa' }}>{playerStats.wins}:{playerStats.losses}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>W/L</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 12 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{playerStats.avg_weekly_pts ? Math.round(playerStats.avg_weekly_pts) : '–'}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>∅ Weekly Pts</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{playerStats.last_week_pts ? Math.round(playerStats.last_week_pts) : '–'}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Last Week</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{playerStats.wins + playerStats.losses}</div>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase' }}>Games</div>
            </div>
          </div>
        </div>
      )}

      <EloHistoryChart selectedPlayerId={targetId} />

      {/* ── Weekly Points Bar Chart ── */}
      {weeklyChartData && (
        <div className="profile-section" style={{ margin: '0 16px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: '#8890a4', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Weekly Points (last {weeklyChartData.weeks.length} weeks)</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', color: '#888' }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#c53616' }} /> Your Points
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#2573d3', opacity: 0.6, marginLeft: 8 }} /> Opp. Points
            </span>
          </div>
          <svg viewBox={`0 0 ${weeklyChartData.weeks.length * 76 + 16} 210`} style={{ width: '100%', maxWidth: weeklyChartData.weeks.length * 76 + 16 }}>
            <line x1={0} y1={weeklyChartData.avgY} x2={weeklyChartData.weeks.length * 76 + 16} y2={weeklyChartData.avgY} stroke="#ffb63c" strokeWidth="2" strokeDasharray="6 4" />
            <text x={weeklyChartData.weeks.length * 76 + 12} y={weeklyChartData.avgY - 5} textAnchor="end" fontSize="10" fill="#ffb63c" fontWeight="700">avg {Math.round(weeklyChartData.avg)}P</text>
            {weeklyChartData.weeks.map(([label, data], i) => {
              const x = 16 + i * 76;
              const myH = (data.myPts / weeklyChartData.maxPts) * 150;
              const oppH = (data.oppPts / weeklyChartData.maxPts) * 150;
              const isWin = data.myPts > data.oppPts;
              return (
                <g key={label}>
                  <rect x={x} y={180 - myH} width={28} height={myH} rx={4} fill={isWin ? '#c53616' : '#8b3a2a'} />
                  <text x={x + 14} y={176 - myH} textAnchor="middle" fontSize="10" fontWeight="700" fill="#c53616">{data.myPts}</text>
                  <rect x={x + 32} y={180 - oppH} width={28} height={oppH} rx={4} fill={!isWin ? '#2573d3' : '#1a4a7a'} opacity={0.6} />
                  <text x={x + 46} y={176 - oppH} textAnchor="middle" fontSize="10" fontWeight="700" fill="#2573d3">{data.oppPts}</text>
                  <text x={x + 30} y={196} textAnchor="middle" fontSize="9" fill="#888">{(label ?? '').replace('20', '')}</text>
                  <text x={x + 30} y={206} textAnchor="middle" fontSize="8" fill="#666">{data.wins}W/{data.losses}L</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {/* ── Points by Sport (last 4 weeks) ── */}
      {sportsActivities.length > 0 && (
        <div style={{ margin: '0 16px 16px' }}>
          <SportBreakdownBar activities={sportsActivities} currentDayIndex={6} visible={true} title="Points by sport (last 10 weeks)" />
        </div>
      )}

      {/* ── HR Zones ── */}
      <div className="profile-content">
        {loading ? (
          <p className="profile-loading">Loading...</p>
        ) : hrZones ? (
          <>
            <div className="sport-breakdown-bar sport-breakdown-bar--visible" style={{ marginBottom: 16 }}>
              <div className="sdb-header">
                <span className="sdb-title">Heart Rate Zones of {playerName} (last 10 weeks)</span>
                {Object.keys(zoneBreakdown).length > 0 && (
                  <span className="sdb-total">{Math.round(Object.values(zoneBreakdown).reduce((s, v) => s + v, 0) / 60)}min</span>
                )}
              </div>
              <div className="sdb-bar-track">
                {(() => {
                  const totalSec = Object.values(zoneBreakdown).reduce((s, v) => s + v, 0);
                  if (totalSec === 0) {
                    // Fallback: use percentage ranges if no actual data
                    return Object.entries(hrZones.zone_ranges).map(([zone, _range]) => {
                      const z = parseInt(zone);
                      const pct = hrZones.zone_percentages[z];
                      const widthPct = pct[1] - pct[0];
                      return (
                        <div key={zone} className="sdb-bar-seg" style={{ backgroundColor: ZONE_COLORS[z], width: `${widthPct}%`, transitionDuration: '500ms' }}>
                          {widthPct >= 5 && (
                            <span className="sdb-bar-seg-label">
                              <span className="sdb-bar-seg-label-top">
                                <span className="sdb-bar-seg-label-pct">{Math.round(widthPct)}%</span>
                              </span>
                              <span className="sdb-bar-seg-label-name">Z{z}</span>
                            </span>
                          )}
                        </div>
                      );
                    });
                  }
                  // Actual zone time proportions
                  let cumPct = 0;
                  return Object.entries(zoneBreakdown).map(([zone, sec]) => {
                    const z = parseInt(zone);
                    const pct = (sec / totalSec) * 100;
                    const left = cumPct;
                    cumPct += pct;
                    return (
                      <div key={zone} className="sdb-bar-seg" style={{ backgroundColor: ZONE_COLORS[z] || '#6b7280', width: `${pct}%`, transitionDuration: '500ms' }}>
                        {pct >= 5 && (
                          <span className="sdb-bar-seg-label">
                            <span className="sdb-bar-seg-label-top">
                              <span className="sdb-bar-seg-label-pct">{Math.round(pct)}%</span>
                            </span>
                            <span className="sdb-bar-seg-label-name">Z{z}</span>
                          </span>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="sdb-legend">
                {Object.entries(zoneBreakdown).length > 0
                  ? Object.entries(zoneBreakdown).map(([zone, sec]) => {
                      const z = parseInt(zone);
                      const totalSec = Object.values(zoneBreakdown).reduce((s, v) => s + v, 0);
                      const pct = totalSec > 0 ? (sec / totalSec) * 100 : 0;
                      return (
                        <div key={zone} className="sdb-legend-item">
                          <span className="sdb-legend-dot" style={{ background: ZONE_COLORS[z] || '#6b7280' }} />
                          <span className="sdb-legend-label">Z{z}: {ZONE_NAMES[z]}</span>
                          <span className="sdb-legend-pts">{Math.round(pct)}%</span>
                        </div>
                      );
                    })
                  : Object.entries(hrZones.zone_ranges).map(([zone]) => {
                      const z = parseInt(zone);
                      const pct = hrZones.zone_percentages[z];
                      const widthPct = pct[1] - pct[0];
                      return (
                        <div key={zone} className="sdb-legend-item">
                          <span className="sdb-legend-dot" style={{ background: ZONE_COLORS[z] }} />
                          <span className="sdb-legend-label">Z{z}: {ZONE_NAMES[z]}</span>
                          <span className="sdb-legend-pts">{Math.round(widthPct)}%</span>
                        </div>
                      );
                    })
                }
              </div>
            </div>
            <div className="profile-section">
              <h2 style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8890a4', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Heart Rate Zones of {playerName}</h2>
              <p className="profile-subtitle">Calculated max HR: <strong>{hrZones.calculated_max_hr} bpm</strong></p>
              {hrZones.zone_ranges && Object.keys(hrZones.zone_ranges).length > 0 ? (
                <div className="zone-table">
                  {Object.entries(hrZones.zone_ranges).map(([zone, range]) => {
                    const z = parseInt(zone);
                    const pct = hrZones.zone_percentages[z];
                    return (
                      <div className="zone-row" key={zone}>
                        <div className="zone-indicator" style={{ backgroundColor: ZONE_COLORS[z] }} />
                        <div className="zone-info">
                          <span className="zone-name">Zone {z}: {ZONE_NAMES[z]}</span>
                          <span className="zone-bpm">{range.min} – {range.max === 999 ? '∞' : range.max} bpm</span>
                          <span className="zone-pct">{pct[0]}% – {pct[1]}% of max HR</span>
                        </div>
                        <div className="zone-bar">
                          <div className="zone-bar-fill" style={{ backgroundColor: ZONE_COLORS[z], left: `${Math.max(0, (pct[0] - 40) / 60 * 100)}%`, width: `${(pct[1] - Math.max(pct[0], 40)) / 60 * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="profile-no-data">No heart rate zones available.</p>
              )}
            </div>
            <div className="profile-section profile-disclaimer">
              <p>We use data from Strava and our own calculations to make your zones as fair as possible. Unfortunately, this is always just an estimate — we ask for your understanding.</p>
            </div>

            {/* ── Badges Section ── */}
            {badges.length > 0 && (
              <div className="profile-section">
                <div style={{ fontWeight: 700, color: '#8890a4', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Badges
                </div>
                <div style={{ fontSize: '0.72rem', color: '#888', lineHeight: 1.5, marginBottom: 16 }}>
                  Badges are earned after each weekly duel when you meet specific goals. Once earned, the weekly reward is permanent. You can advance one tier per badge per week, but multiple badges can level up in the same week.
                </div>
                {badges.map((badge: any) => {
                  const earned = badge.current_tier > 0;
                  const yellowColor = '#e6b800';
                  return (
                    <div key={badge.id} className="profile-badge-row">
                      {/* Badge Icon */}
                      <div className="profile-badge-main-icon">
                        <div className="profile-badge-main-circle" style={{
                          background: earned ? 'rgba(230,184,0,0.15)' : 'rgba(230,184,0,0.06)',
                        }}>
                          <FontAwesomeIcon icon={getSportIcon(badge.icon || 'BadgeReward')} />
                        </div>
                      </div>
                      <div className="profile-badge-info">
                        <div className="profile-badge-name">{badge.name}</div>
                        <div className="profile-badge-desc">{getBadgeCurrentValue(badge)}</div>
                        <BadgeTimeline badge={badge} activeTier={activeTimelineTier} setActiveTier={setActiveTimelineTier} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <p className="profile-no-data">No profile data available.</p>
        )}
      </div>
    </div>
  );
}
